<!--
  CommsSuppressionManager — reusable suppression management UI.

  Used both as the standalone /admin-comms-suppressions page body
  and as the inline body of the "Suppressions" sibling tab on /admin-comms.

  Encapsulates: filter row, paginated table, add dialog, remove dialog,
  snackbar. Backend endpoints: /admin/comms/suppressions (GET/POST/DELETE).
-->
<script setup>
import { ref, onMounted } from 'vue'
import axiosIns from '@/plugins/axios'

const loading = ref(false)
const suppressions = ref([])
const total = ref(0)
const page = ref(1)
const itemsPerPage = ref(50)
const searchEmail = ref('')
const reasonFilter = ref('')
const activeOnly = ref(true)
const searchDebounce = ref(null)

const addDialog = ref(false)

// Manual suppressions default to bypassableForTransactional=true (admin opt-out is
// usually about marketing, not service email; RM-446); SNS-driven reasons default to false.
const addForm = ref({ email: '', reason: 'manual', detail: '', bypassableForTransactional: true })
const addLoading = ref(false)
const addError = ref('')

function onAddReasonChange(newReason) {
  addForm.value.bypassableForTransactional = newReason === 'manual'
}

const removeDialog = ref(false)
const removeTarget = ref(null)
const removalReason = ref('')
const removeLoading = ref(false)

const snackbar = ref({ show: false, message: '', color: 'success' })

const reasonColors = {
  hard_bounce: 'error',
  complaint: 'deep-orange',
  manual: 'warning',
  soft_bounce_threshold: 'orange',
}

const reasonLabels = {
  hard_bounce: 'Hard Bounce',
  complaint: 'Complaint',
  manual: 'Manual',
  soft_bounce_threshold: 'Soft Bounce Threshold',
}

const reasonOptions = [
  { title: 'All Reasons', value: '' },
  { title: 'Hard Bounce', value: 'hard_bounce' },
  { title: 'Complaint', value: 'complaint' },
  { title: 'Manual', value: 'manual' },
  { title: 'Soft Bounce Threshold', value: 'soft_bounce_threshold' },
]

const addReasonOptions = [
  { title: 'Manual', value: 'manual' },
  { title: 'Hard Bounce', value: 'hard_bounce' },
  { title: 'Complaint', value: 'complaint' },
  { title: 'Soft Bounce Threshold', value: 'soft_bounce_threshold' },
]

async function fetchSuppressions() {
  loading.value = true
  try {
    const params = {
      page: page.value,
      limit: itemsPerPage.value,
      activeOnly: activeOnly.value,
    }
    if (searchEmail.value) params.email = searchEmail.value
    if (reasonFilter.value) params.reason = reasonFilter.value

    const res = await axiosIns.get('/admin/comms/suppressions', { params })
    suppressions.value = res.data.data.docs
    total.value = res.data.data.total
  } catch (err) {
    showSnackbar('Failed to load suppressions', 'error')
  } finally {
    loading.value = false
  }
}

function onSearchChange() {
  clearTimeout(searchDebounce.value)
  searchDebounce.value = setTimeout(() => {
    page.value = 1
    fetchSuppressions()
  }, 300)
}

function onPageChange(newPage) {
  page.value = newPage
  fetchSuppressions()
}

function openAddDialog() {
  addForm.value = { email: '', reason: 'manual', detail: '', bypassableForTransactional: true }
  addError.value = ''
  addDialog.value = true
}

async function submitAdd() {
  addLoading.value = true
  addError.value = ''
  try {
    await axiosIns.post('/admin/comms/suppressions', addForm.value)
    addDialog.value = false
    showSnackbar('Suppression added', 'success')
    fetchSuppressions()
  } catch (err) {
    addError.value = err.response?.data?.error || 'Failed to add suppression'
  } finally {
    addLoading.value = false
  }
}

function openRemoveDialog(suppression) {
  removeTarget.value = suppression
  removalReason.value = ''
  removeDialog.value = true
}

async function submitRemove() {
  if (!removalReason.value?.trim()) return
  removeLoading.value = true
  try {
    await axiosIns.delete(`/admin/comms/suppressions/${removeTarget.value._id}`, {
      data: { removalReason: removalReason.value },
    })
    removeDialog.value = false
    showSnackbar('Suppression removed', 'success')
    fetchSuppressions()
  } catch (err) {
    showSnackbar(err.response?.data?.error || 'Failed to remove suppression', 'error')
  } finally {
    removeLoading.value = false
  }
}

function showSnackbar(message, color = 'success') {
  snackbar.value = { show: true, message, color }
}

function formatDate(d) {
  if (!d) return '—'
  
  return new Date(d).toLocaleString()
}

onMounted(fetchSuppressions)
</script>

<template>
  <div class="comms-suppression-manager">
    <VRow
      class="mb-2"
      align="center"
    >
      <VCol>
        <p class="text-body-2 text-medium-emphasis ma-0">
          Addresses suppressed from receiving emails. Hard bounces and complaints are added automatically via SES SNS notifications.
        </p>
      </VCol>
      <VCol cols="auto">
        <VBtn
          color="primary"
          size="small"
          @click="openAddDialog"
        >
          Add Suppression
        </VBtn>
      </VCol>
    </VRow>

    <!-- Filters -->
    <VRow
      class="mb-3"
      dense
    >
      <VCol
        cols="12"
        sm="4"
      >
        <VTextField
          v-model="searchEmail"
          label="Search by email"
          clearable
          density="compact"
          hide-details
          @update:model-value="onSearchChange"
        />
      </VCol>
      <VCol
        cols="12"
        sm="3"
      >
        <VSelect
          v-model="reasonFilter"
          :items="reasonOptions"
          item-title="title"
          item-value="value"
          label="Reason"
          density="compact"
          hide-details
          @update:model-value="fetchSuppressions"
        />
      </VCol>
      <VCol
        cols="12"
        sm="3"
      >
        <VSwitch
          v-model="activeOnly"
          label="Active only"
          density="compact"
          hide-details
          @update:model-value="fetchSuppressions"
        />
      </VCol>
    </VRow>

    <!-- Table -->
    <VCard>
      <VDataTableServer
        :items="suppressions"
        :items-length="total"
        :loading="loading"
        :items-per-page="itemsPerPage"
        :page="page"
        :headers="[
          { title: 'Email', key: 'email' },
          { title: 'Reason', key: 'reason' },
          { title: 'Detail', key: 'detail' },
          { title: 'Suppressed At', key: 'suppressedAt' },
          { title: 'Suppressed By', key: 'suppressedBy', sortable: false },
          { title: 'Removed At', key: 'removedAt' },
          { title: 'Actions', key: 'actions', sortable: false },
        ]"
        @update:page="onPageChange"
      >
        <template #item.reason="{ item }">
          <VChip
            :color="reasonColors[item.reason] || 'grey'"
            size="small"
            label
          >
            {{ reasonLabels[item.reason] || item.reason }}
          </VChip>
        </template>
        <template #item.suppressedAt="{ item }">
          {{ formatDate(item.suppressedAt) }}
        </template>
        <template #item.suppressedBy="{ item }">
          <span v-if="item.suppressedBy && (item.suppressedBy.firstName || item.suppressedBy.email)">
            {{ [item.suppressedBy.firstName, item.suppressedBy.lastName].filter(Boolean).join(' ') || item.suppressedBy.email }}
          </span>
          <span
            v-else
            class="text-medium-emphasis text-caption"
          >Automatic (SES)</span>
        </template>
        <template #item.removedAt="{ item }">
          <span
            v-if="item.removedAt"
            class="text-success"
          >{{ formatDate(item.removedAt) }}</span>
          <VChip
            v-else
            color="error"
            size="small"
          >
            Active
          </VChip>
        </template>
        <template #item.actions="{ item }">
          <VBtn
            v-if="!item.removedAt"
            size="small"
            color="warning"
            variant="tonal"
            @click="openRemoveDialog(item)"
          >
            Remove
          </VBtn>
        </template>
      </VDataTableServer>
    </VCard>

    <!-- Add Suppression Dialog -->
    <VDialog
      v-model="addDialog"
      max-width="500"
    >
      <VCard>
        <VCardTitle>Add Suppression</VCardTitle>
        <VCardText>
          <VTextField
            v-model="addForm.email"
            label="Email address"
            required
            class="mb-3"
          />
          <VSelect
            v-model="addForm.reason"
            :items="addReasonOptions"
            item-title="title"
            item-value="value"
            label="Reason"
            class="mb-3"
            @update:model-value="onAddReasonChange"
          />
          <VTextField
            v-model="addForm.detail"
            label="Detail / Note (optional)"
            class="mb-3"
          />
          <VSwitch
            v-model="addForm.bypassableForTransactional"
            label="Allow transactional emails (bypass for password reset etc)"
            density="compact"
          />
          <VAlert
            v-if="addError"
            type="error"
            density="compact"
            class="mt-2"
          >
            {{ addError }}
          </VAlert>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn @click="addDialog = false">
            Cancel
          </VBtn>
          <VBtn
            color="primary"
            :loading="addLoading"
            @click="submitAdd"
          >
            Add
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Remove Suppression Dialog -->
    <VDialog
      v-model="removeDialog"
      max-width="450"
    >
      <VCard>
        <VCardTitle>Remove Suppression</VCardTitle>
        <VCardText>
          <p class="mb-3">
            Removing suppression for <strong>{{ removeTarget?.email }}</strong>.
          </p>
          <VAlert
            v-if="removeTarget?.reason === 'hard_bounce'"
            type="warning"
            density="compact"
            class="mb-3"
          >
            This address hard-bounced. Only remove if the member has verified a corrected email address.
          </VAlert>
          <VTextField
            v-model="removalReason"
            label="Removal reason (required)"
            required
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn @click="removeDialog = false">
            Cancel
          </VBtn>
          <VBtn
            color="error"
            :loading="removeLoading"
            :disabled="!removalReason.trim()"
            @click="submitRemove"
          >
            Remove
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VSnackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="3000"
    >
      {{ snackbar.message }}
    </VSnackbar>
  </div>
</template>
